import React, { useEffect, useState } from 'react'
import ReservationBoxCheckout from './ReservationBoxCheckout'
import { PaymentAPI } from '../../api/PaymentAPI'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Accordion, AccordionDetails, AccordionSummary, Button, Typography } from '@mui/material'
import { rootAction } from '../../redux/reducer/rootReducer'
import Loader from '../../component/Loader'
import { IoIosClose } from "react-icons/io";
import { IoIosArrowDown } from "react-icons/io";
import PopUpAlert from '../../component/PopUpAlert'
import { CalendarAPI } from '../../api/CalendarAPI'
import { canConfirmZeroReservation, formatCheckoutMoney, reservationPaymentForUserId, ZERO_COURT_PRICE_COPY } from '../../util/calendarAccess'
import {
    CHECKOUT_VENDOR_REQUIRED,
    FINANCIAL_VENDOR_PATH,
    checkoutVendorErrorMessage,
} from '../../util/financialVendorGate'

const CartDetails = () => {
    document.title = "Reservation Checkout"
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const { isPaymentDoneForReservation } = useSelector((store) => store.root);
    const [checkoutDetails, setCheckoutDetails] = useState('');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loadingCount, setLoadingCount] = useState(0);
    const [priceBreakdown, setPriceBreakdown] = useState({});
    const [expanded, setExpanded] = useState({});
    const [vendorRequired, setVendorRequired] = useState(false);

    const handleExpansion = (index) => {
        setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const startLoading = () => {
        setLoadingCount(prevCount => prevCount + 1);
    };
    const finishLoading = () => {
        setLoadingCount(prevCount => prevCount - 1);
    };

    useEffect(() => {
        if (selectedClub.clubId && userInfo.userId) {
            calculatePricing();
        }
    }, [userInfo.userId, selectedClub.clubId, isPaymentDoneForReservation.status])

    const calculatePricing = async () => {
        try {
            const paymentInfo = {
                userId: userInfo.userId,
                clubId: selectedClub.clubId
            }
            startLoading();
            const response = await PaymentAPI.postCalculatePricing(paymentInfo);
            setCheckoutDetails(response.Data);
            let data = response.Data.priceBreakdown;
            const priceBreakdownData = new Map();
            for (let dataKey in data) {
                const newPriceBreakdown = [];
                for (let key in data[dataKey]) {
                    const splitKey = key.split('-');
                    if (Number(data[dataKey][key]) != 0) {
                        newPriceBreakdown.push({
                            start: splitKey[0],
                            end: splitKey[1],
                            price: splitKey[2],
                            dayType: splitKey[3],
                            hours: data[dataKey][key]
                        });
                    }
                }
                priceBreakdownData.set(dataKey, newPriceBreakdown);
            }
            setPriceBreakdown(priceBreakdownData);
        } catch (error) {
            console.error(error);
        } finally {
            finishLoading();
        }

    }
    const handleConfirmFreeReservation = async () => {
        try {
            startLoading();
            const response = await CalendarAPI.postConfirmFreeReservation(userInfo.userId, selectedClub.clubId);
            dispatch(rootAction.setReservationPaymentStatus(({ status: true, type: 'payment' })));
            finishLoading();
            dispatch(rootAction.setWarningMessage({
                id: Date.now(),
                type: 'success',
                content: 'This reservation was free. It is now confirmed.',
            }));
            setTimeout(() => {
                 dispatch(rootAction.setIsUpdateTotalSchedule(true));
            }, 1000);
            navigate('/');

            finishLoading();
        } catch (error) {
            finishLoading();
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: 'Something went wrong' }));
        }
    }

    const chargeAmount = formatCheckoutMoney(checkoutDetails?.totalAmount);

    const handleCheckout = async () => {

        if (checkoutDetails?.warning) {
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: checkoutDetails?.warning }));
            return;
        }

        if (chargeAmount === '0.00') {
            if (canConfirmZeroReservation(userInfo)) {
                handleConfirmFreeReservation();
            } else {
                dispatch(rootAction.setWarningMessage({
                    id: Date.now(),
                    type: 'error',
                    content: ZERO_COURT_PRICE_COPY,
                }));
            }
            return;
        }
        try {
            const paymentDetails = {
                userId: reservationPaymentForUserId(userInfo, checkoutDetails?.reservations),
                paymentCreatedBy: userInfo.userId,
                clubId: selectedClub.clubId,
                amount: chargeAmount,
                paymentType: "onReservationPayment",
            }
            startLoading();
            const paymentIntentResponse = await PaymentAPI.postCreatePaymentIntent(paymentDetails);
            dispatch(rootAction.setPaymentIntentId(paymentIntentResponse.Data.paymentIntentId));
            finishLoading();
            navigate('/payment', {
                state: {
                    clientSecret: paymentIntentResponse.Data.paymentIntentClientSecret,
                    paymentIntentId: paymentIntentResponse.Data.paymentIntentId,
                    reservationFor: paymentDetails.userId,
                    priceDetails: {
                        totalHour: checkoutDetails?.totalHour,
                        totalReservations: checkoutDetails?.totalReservations,
                        totalAmount: paymentIntentResponse.Data.totalAmount || chargeAmount,
                        processingFee: paymentIntentResponse.Data.processingFee,
                    },
                }
            })
        } catch (error) {
            finishLoading();
            const vendorMessage = checkoutVendorErrorMessage(error);
            if (vendorMessage) {
                setVendorRequired(true);
                dispatch(rootAction.setWarningMessage({
                    id: Date.now(),
                    type: 'error',
                    content: vendorMessage,
                }));
            }
        }
    }
    const showInitialLoader = loadingCount > 0 && !checkoutDetails;

    if (showInitialLoader) {
        return <Loader />
    }

    return (
        <div className='cart_page_container'>
            {loadingCount > 0 && <Loader />}
            {checkoutDetails && checkoutDetails.reservations.length != 0
                ?
                <>
                    <div className="reservation_details_for_checkout" >
                        {/* Timer lives in the header / cart badge so this page does not mount a second clock. */}
                        <h1>Reservation Details:</h1>

                        {checkoutDetails?.reservations?.map((item) =>
                            <ReservationBoxCheckout
                                key={item.reservationId}
                                selectedClub={selectedClub}
                                startLoading={startLoading}
                                finishLoading={finishLoading}
                                reservation={item}
                                isReservation={true}
                            />
                        )}
                    </div>
                    <div className="reservation_price_detals_body">
                        <h1>Price Details</h1>
                        <div className="reservation_price_detals_row">
                            <p>{Number(checkoutDetails?.totalReservations) > 1 ? 'Total Reservations' : 'Total Reservation'}</p>
                            <p>{checkoutDetails?.totalReservations}</p>
                        </div>
                        <div className="reservation_price_detals_row">
                            <p>{Number(checkoutDetails?.totalHour) > 1 ? 'Total Hours' : 'Total Hour'}</p>
                            <p>{checkoutDetails?.totalHour}</p>
                        </div>
                        <div className="reservation_horizontal_line"></div>
                        {[...(priceBreakdown instanceof Map ? priceBreakdown.entries() : [])].map(([name, priceList], index) =>
                        (<Accordion
                            expanded={expanded[index]}
                            onChange={() => handleExpansion(index)}
                            sx={{
                                boxShadow: 'none',
                                border: '1px solid #E5E5E5',
                                borderRadius: '5px',
                                marginBottom: '10px'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<IoIosArrowDown />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography component="span">{name?.slice(37)}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {priceList.length != 0 && priceList.map((item) =>
                                    <div className="reservation_price_detals_row">
                                        <p>{item.dayType + '('}
                                            {(Number(item.start) / 60) >= 12
                                                ? (Number(item.start) / 60) == 12
                                                    ? (Number(item.start) / 60)
                                                    : (Number(item.start) / 60) - 12
                                                : (Number(item.start) / 60)}{(Number(item.start) / 60) >= 12 ? 'pm' : 'am'}
                                            {' - '}
                                            {(Number(item.end) / 60) >= 12
                                                ? (Number(item.end) / 60) == 12
                                                    ? (Number(item.end) / 60) : (Number(item.end) / 60) - 12
                                                : (Number(item.end) / 60)}{(Number(item.end) / 60) >= 12 ? 'pm)' : 'am)'}
                                        </p>
                                        <p className='price-break-down-price'>${Number(item?.price)?.toFixed(2)}<IoIosClose />{Number(item.hours) / 60}{(Number(item.hours) / 60) > 1 ? ' Hours' : ' Hour'}</p>
                                    </div>
                                )}
                            </AccordionDetails>
                        </Accordion>))}

                        <div className="reservation_horizontal_line"></div>
                        <div className="reservation_price_detals_row">
                            <p>Total Amount:</p>
                            <p data-testid="cart-total-amount">${formatCheckoutMoney(chargeAmount)}</p>
                        </div>
                        <button onClick={handleCheckout}>checkout</button>
                        {vendorRequired &&
                            <Button
                                data-testid="financial-vendor-link"
                                onClick={() => navigate(FINANCIAL_VENDOR_PATH)}
                            >
                                {CHECKOUT_VENDOR_REQUIRED}
                            </Button>}
                    </div>
                </>
                :
                <div className='checkout_reservation_not_found' data-testid="empty-reservation-cart">
                    <div className="empty-cart-card">
                        <p className="empty-cart-kicker">Cart</p>
                        <h1>Nothing to check out yet</h1>
                        <p className="empty-cart-lead">Hold a court on the reservation calendar, then come back here to pay.</p>
                        <Button
                            data-testid="empty-cart-book"
                            onClick={() => {
                                navigate('/calendar');
                                dispatch(rootAction.setSlideBarLink('calendar'));
                            }}
                            variant='contained'
                        >
                            Open reservation calendar
                        </Button>
                    </div>
                </div>}
        </div>
    )
}

export default CartDetails